<script lang="ts" setup>
import { computed, inject, ref } from 'vue'
import type { Ref } from 'vue'
import type { ChapterConfig } from '@/composables/payment'
import { PaymentInlinePanel } from '@/composables/payment'
import { CHAPTER_LOCK, CHARGE_TYPE } from '@/constants'
import { useI18n } from '@/composables/useI18n'

interface Props {
  chapter: any
  contentId: string
  contentChapterId: string
  bookName?: string
  serialStatus?: number
  // recommend 模式下章节虽未锁但仍强制展示支付版位(隐藏引导文案/Unlock 按钮/遮罩)
  forceShowPanel?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'pay-success'): void
}>()

const config = inject<ChapterConfig>('chapterConfig')!
const theme = inject<Ref<{ background: string; size: string }>>('theme')
const autoLock = ref(true)
// 无充值档位且订阅全为畅读会员时由 PaymentInlinePanel 上报
const membershipOnly = ref(false)
const { t } = useI18n()

const themeBackground = computed(() => theme?.value?.background ?? '#F6F7F9')

// 夜间皮肤判定：按主题背景色亮度，低亮度即深色（用于支付方式选择器的暗色样式/白色图标）
const isDarkTheme = computed(() => {
  const bg = themeBackground.value
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(bg || '')
  if (!m)
    return false
  const r = Number.parseInt(m[1], 16)
  const g = Number.parseInt(m[2], 16)
  const b = Number.parseInt(m[3], 16)
  // 相对亮度（0~255），阈值 80 以下视为深色
  return (0.299 * r + 0.587 * g + 0.114 * b) < 80
})

const isLocked = computed(() => props.chapter?.lockStatus === CHAPTER_LOCK.LOCK)
const shouldShow = computed(() => isLocked.value || props.forceShowPanel)

function toggleAutoLock() {
  autoLock.value = !autoLock.value
}

function handlePaySuccess() {
  emit('pay-success')
}

defineExpose({
  autoLock,
})
</script>

<template>
  <div v-if="shouldShow" class="pt-40px chapter-payment text-center">
    <!-- 引导区(仅锁章时展示) -->
    <template v-if="isLocked">
      <!-- 装饰分割线 + 锁图标 -->
      <div class="unlock-divider flex items-center justify-center mb-16px">
        <span class="unlock-divider-line" />
        <span class="unlock-divider-icon mx-8px" aria-hidden="true" />
        <span class="unlock-divider-line" />
      </div>

      <!-- 引导文案 -->
      <!-- 仅订阅(畅读会员)场景: 统一文案, 不展示金币解锁相关 -->
      <template v-if="membershipOnly">
        <h3 class="theme-h3 font-bold text-18px text-center">
          {{ config.texts.unlockTitle }}
        </h3>
        <p class="theme-desc mt-8px lh-24px text-14px text-center" style="white-space: pre-line;">
          {{ t('UnlockMembership') }}
        </p>
      </template>
      <template v-else>
        <h3 class="theme-h3 font-bold text-18px text-center">
          {{ config.texts.unlockTitle }}
        </h3>
        <p class="theme-desc mt-8px lh-24px text-14px text-center" style="white-space: pre-line;">
          <template v-if="chapter.chargeType === CHARGE_TYPE.CHAPTER">
            {{ t('CoinsToRead2', { 0: chapter.priceAmount }) }}
          </template>
          <template v-else>
            {{ t('CoinsToReadBook', { 0: chapter.priceAmount }) }}
          </template>
        </p>
      </template>
    </template>

    <!-- 支付版位:锁章时展示(余额足够会自动解锁,不会进入此处);或 forceShowPanel 强制展示 -->
    <div v-if="isLocked || forceShowPanel" class="mt-24px" :class="{ 'pb-18px': membershipOnly }">
      <PaymentInlinePanel
        :content-id="contentId"
        :content-chapter-id="contentChapterId"
        :book-name="bookName || ''"
        :chapter-name="chapter?.contentChapterName || ''"
        :serial-status="serialStatus || 0"
        :dark="isDarkTheme"
        @success="handlePaySuccess"
        @update:membership-only="membershipOnly = $event"
      />
    </div>

    <!-- 自动解锁开关(仅订阅场景隐藏) -->
    <div v-if="!membershipOnly" class="theme-radio mt-24px flex justify-center items-center" @click.stop="toggleAutoLock">
      <div class="radio auto-lock-radio flex justify-center items-center w-14px h-14px inline-block border b-rd-14px cursor" :class="{ selected: autoLock }">
        <span class="inline-block w-6px h-6px bg-n-primary b-rd-6px" />
      </div>
      <span class="ml-6px label theme-desc text-14px">{{ config.texts.autoUnlock }}</span>
    </div>

    <!-- Coins 余额(仅订阅场景隐藏) -->
    <p v-if="!membershipOnly" class="theme-balance mt-12px pb-18px lh-24px text-14px text-center">
      {{ t('NovelBalance', { 0: chapter.memberCoin }) }}
    </p>

    <!-- 渐变遮罩(仅锁章时盖住章节末尾) -->
    <div v-if="isLocked" class="mask" />
  </div>
</template>

<style lang="scss" scoped>
.radio {
  span {
    display: none;
  }
  &.selected {
    border-color: var(--primary-color);
    span {
      display: inline-block
    }
  }
}

.auto-lock-radio {
  border-color: var(--theme-balance-color, #9296A0);
}

.chapter-payment {
  position: relative;
  .mask {
    position: absolute;
    top: -126px;
    left: 0;
    width: 100%;
    height: 126px;
    background: var(--mask-gradient, linear-gradient(180deg, rgba(246,247,249,0.00) 20%, var(--theme-background, #F6F7F9) 100%));
    pointer-events: none;
  }
}

.unlock-divider {
  width: 100%;
}

.unlock-divider-line {
  width: 80px;
  height: 1px;
  background: linear-gradient(to right, transparent, var(--theme-balance-color, rgba(146, 150, 160, 0.4)), transparent);
  opacity: 0.5;
}

.unlock-divider-icon {
  display: inline-block;
  width: 16px;
  height: 18px;
  flex-shrink: 0;
  background-color: var(--theme-balance-color, #9296A0);
  -webkit-mask-image: url('/images/chapter-lock.svg');
  mask-image: url('/images/chapter-lock.svg');
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}
</style>
